import anime from 'animejs'
import {ConnectedComponent,sharedComponent} from 'lowkey-component'

import {Debug} from '@helpers/Debug'
import {sleep} from '@lib/utils'
import {createApplicationState} from '@services/ApplicationState'
import {MediaQuery} from '@services/MediaQuery'
import {IApplicationState} from '@services/types'
import {appSelectors} from '@store/appSlice'

const maxRatioDesktop = 0.6921875
const maxRatio = 0.8
const minWidth = 86

@sharedComponent
export class SiteIndicator extends ConnectedComponent<HTMLDivElement> {

  private mq = MediaQuery.create()
  private appState: IApplicationState = createApplicationState()
  private loading: HTMLDivElement | undefined
  private logo: HTMLDivElement | undefined
  private str: HTMLDivElement | undefined
  private copy: HTMLDivElement | undefined
  private backdrop: HTMLDivElement | undefined
  private inner: HTMLDivElement | undefined

  // didMountのタイミングでは、selectSiteIndicatorPercentが発火しないのでconstructorで
  constructor(element: HTMLDivElement) {
    super(element)

    this.logo = this.element?.querySelector('.o-site-indicator_logo_logo') as HTMLDivElement
    this.str = this.element?.querySelector('.o-site-indicator_logo_str') as HTMLDivElement
    this.copy = this.element?.querySelector('.o-site-indicator_logo_copy') as HTMLDivElement
    this.loading = this.element?.querySelector('.o-site-indicator_loading') as HTMLDivElement
    this.backdrop = this.element?.querySelector('.o-site-indicator_backdrop') as HTMLDivElement
    this.inner = this.element?.querySelector('.o-site-indicator_inner') as HTMLDivElement

    this.observe(appSelectors.selectSiteIndicatorStatus, async (status) => {
      if (status === 'hide') {
        this.appState.updateSiteIndicatorStatus('startHideAnimation')
        await sleep(1900)
        this.appState.updateSiteIndicatorStatus('hidingAnimation')
      }
    })

    this.observe(appSelectors.selectSiteIndicatorPercent, (percent) => {
      if (Debug.isDebugMode()) {
        this.appState.updateSiteIndicatorStatus('endHideAnimation')
        return;
      }

      anime({
        targets: this.loading,
        duration: 1700,
        easing: 'easeOutExpo',
        width: this.getWidth(percent),
        opacity: this.getOpacity(percent),
        complete: () => {
          if (percent !== 1) {
            return
          }
          const {width = 0} = this.str?.getBoundingClientRect() || {}
          const tl = anime.timeline({
            targets: this.loading,
            duration: 300,
            easing: 'easeOutExpo',
          })
          tl.add({
            duration: 500,
            targets: this.loading,
            easing: 'easeOutExpo',
            width: [this.getWidth(1), width],
          })
          tl.add({
            targets: [this.logo, this.str, this.copy],
            duration: 1000,
            opacity: [0, 1],
            easing: 'easeOutCirc',
          }, '-=100')
          tl.add({
            targets: this.loading,
            duration: 1000,
            opacity: [1, 0],
            easing: 'easeOutCirc',
          }, '-=1100')
          tl.add({
            duration: 1300,
            targets: this.logo,
            translateY: [-15, 0],
            easing: 'easeOutQuart',
          }, '-=1100')
          tl.add({
            duration: 1300,
            targets: this.copy,
            translateY: [15, 0],
            easing: 'easeOutQuart',
          }, '-=1400')
          tl.add({
            targets: this.backdrop,
            duration: 1000,
            translateY: [0, '-100%'],
            easing: 'easeInOutCubic',
          }, '-=800')
          tl.add({
            targets: this.inner,
            duration: 800,
            opacity: 0,
            easing: 'easeOutCubic',
            complete: () => {
              this.element.style.display = 'none'
              this.appState.updateSiteIndicatorStatus('endHideAnimation')
            }
          }, '-=700')

        }
      })
    })

  }

  didMount() {
    const {height = 0} = this.str?.getBoundingClientRect() || {}
    if (this.loading) {
      this.loading.style.height = `${height - 3}px`
    }

    if (Debug.isDebugMode()) {
      this.element.style.display = 'none'
    }

  }

  getMax() {
    return this.mq.isPhone() ? window.innerWidth * maxRatio : window.innerWidth * maxRatioDesktop
  }

  getWidth(per: number) {
    return this.getMax() - this.getMax() * per + minWidth
  }

  getOpacity(per: number) {
    return Math.max(1, per * 1.1)
  }
}
